import { inject } from "@angular/core";
import { CanActivateChildFn, CanActivateFn, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import {
    catchError,
    combineLatest,
    filter,
    map,
    of,
    switchMap,
    take,
} from "rxjs";
import { sessionFeature } from "../../authentication/store/session.reducer";
import { companyEntitlementFeature } from "../store/company-entitlement.reducer";
import { hasPaidFeature, PaidFeature } from "../services/feature-access";
import { CompanyCapacityService } from "../services/company-capacity.service";

export function paidFeatureGuard(feature: PaidFeature): CanActivateFn {
    return () => {
        const store = inject(Store);
        const router = inject(Router);
        return combineLatest([
            store.select(sessionFeature.selectUser),
            store.select(
                companyEntitlementFeature.selectCompanyEntitlementState
            ),
        ]).pipe(
            filter(
                ([user, state]) =>
                    Boolean(user) &&
                    state.clientId === user?.clientId &&
                    (state.status === "loaded" || state.status === "error")
            ),
            take(1),
            map(([, state]) =>
                hasPaidFeature(state.entitlement, feature)
                    ? true
                    : router.createUrlTree(["/dashboard"])
            )
        );
    };
}

const BILLING_ROLES = new Set(["client_owner", "billing", "billing_user"]);

export function hasBillingRole(roles: string[] | undefined): boolean {
    return Boolean(roles?.some((role) => BILLING_ROLES.has(role)));
}

export const billingRoleGuard: CanActivateFn = () => {
    const store = inject(Store);
    const router = inject(Router);
    return store.select(sessionFeature.selectUser).pipe(
        filter(Boolean),
        take(1),
        map((user) =>
            hasBillingRole(user.roles)
                ? true
                : router.createUrlTree(["/plan-expired"])
        )
    );
};

export const companyApplicationAccessGuard: CanActivateFn = () => {
    const store = inject(Store);
    const router = inject(Router);

    return combineLatest([
        store.select(sessionFeature.selectUser),
        store.select(companyEntitlementFeature.selectEntitlement),
    ]).pipe(
        filter(([user]) => Boolean(user)),
        take(1),
        map(([user, entitlement]) => {
            if (!user) return false;
            if (user.role === "owner" || user.role === "partner") return true;
            if (entitlement?.access !== "denied") return true;
            return router.createUrlTree([
                hasBillingRole(user.roles) ? "/shop" : "/plan-expired",
            ]);
        })
    );
};

export const planExpiredGuard: CanActivateFn = () => {
    const store = inject(Store);
    const router = inject(Router);

    return combineLatest([
        store.select(sessionFeature.selectUser),
        store.select(
            companyEntitlementFeature.selectCompanyEntitlementState
        ),
    ]).pipe(
        filter(
            ([user, state]) =>
                Boolean(user) &&
                state.clientId === user?.clientId &&
                (state.status === "loaded" || state.status === "error")
        ),
        take(1),
        map(([, state]) =>
            state.entitlement?.status === "expired"
                ? true
                : router.createUrlTree(["/dashboard"])
        )
    );
};

const CAPACITY_REMEDIATION_ROUTES = [
    "/capacity-required",
    "/shop",
    "/dashboard/users",
    "/dashboard/invites",
];

export const companyCapacityGuard: CanActivateChildFn = (_route, state) => {
    const store = inject(Store);
    const router = inject(Router);
    const capacity = inject(CompanyCapacityService);

    return combineLatest([
        store.select(sessionFeature.selectUser),
        store.select(companyEntitlementFeature.selectCompanyEntitlementState),
    ]).pipe(
        filter(
            ([user, entitlementState]) =>
                Boolean(user) &&
                entitlementState.clientId === user?.clientId &&
                ["loaded", "error"].includes(entitlementState.status)
        ),
        take(1),
        switchMap(([user, entitlementState]) => {
            if (user?.role !== "user" || entitlementState.status === "error")
                return of(true);

            const remediationRoute = CAPACITY_REMEDIATION_ROUTES.some(
                (route) =>
                    state.url === route ||
                    state.url.startsWith(`${route}?`) ||
                    state.url.startsWith(`${route}/`)
            );
            const accessDecision = () => {
                if (!capacity.exceeded() || remediationRoute) return true;
                return router.createUrlTree(["/capacity-required"], {
                    queryParams: { returnUrl: state.url },
                });
            };

            return capacity.ensureCurrent().pipe(
                map(accessDecision),
                // A temporary count-service failure must not lock the company
                // out unless an earlier check already confirmed an overage.
                catchError(() => of(accessDecision()))
            );
        })
    );
};
